import { ETF_CODE } from './holdingParser.js'

export const STORAGE_KEY = 'etf-tracker:v1'
export const USER_SETTINGS_KEY = 'etf-tracker:user-settings:v1'

export function createDefaultComparisonConfigs() {
  return [
    { id: 'c1', daysAgo: 1, metric: 'weight' },
    { id: 'c2', daysAgo: 5, metric: 'weight' },
    { id: 'c3', daysAgo: 10, metric: 'weight' },
    { id: 'c4', daysAgo: 20, metric: 'shares' },
    { id: 'c5', daysAgo: 30, metric: 'shares' },
  ]
}

export function createDefaultStorageState() {
  return {
    version: 1,
    etfCode: ETF_CODE,
    snapshots: [],
    preferences: {
      selectedStockCodes: [],
      comparisonConfigs: createDefaultComparisonConfigs(),
      trendDays: 30,
      trendMetric: 'weight',
      sort: { field: 'weight', direction: 'desc' },
    },
  }
}

export function normalizePreferences(preferences = {}) {
  const defaults = createDefaultStorageState().preferences
  return {
    selectedStockCodes: Array.isArray(preferences.selectedStockCodes)
      ? preferences.selectedStockCodes.map(String)
      : defaults.selectedStockCodes,
    comparisonConfigs: Array.isArray(preferences.comparisonConfigs) && preferences.comparisonConfigs.length === 5
      ? preferences.comparisonConfigs.map((config, index) => ({
          id: config.id ?? `c${index + 1}`,
          daysAgo: clampDaysAgo(config.daysAgo),
          metric: config.metric === 'shares' ? 'shares' : 'weight',
        }))
      : defaults.comparisonConfigs,
    trendDays: clampTrendDays(preferences.trendDays ?? defaults.trendDays),
    trendMetric: preferences.trendMetric === 'shares' ? 'shares' : 'weight',
    sort: preferences.sort?.field
      ? {
          field: preferences.sort.field,
          direction: preferences.sort.direction === 'asc'
            ? 'asc'
            : preferences.sort.direction === null
              ? null
              : 'desc',
        }
      : defaults.sort,
  }
}

export function createDefaultUserSettings() {
  return {
    preferences: createDefaultStorageState().preferences,
    theme: 'dark',
  }
}

export function normalizeUserSettings(settings = {}) {
  const defaults = createDefaultUserSettings()
  return {
    preferences: normalizePreferences(settings.preferences ?? defaults.preferences),
    theme: settings.theme === 'light' ? 'light' : 'dark',
  }
}

export function normalizeTrackerState(state) {
  const defaults = createDefaultStorageState()
  if (state?.version !== 1 || state?.etfCode !== ETF_CODE || !Array.isArray(state.snapshots)) {
    return defaults
  }

  return {
    version: 1,
    etfCode: ETF_CODE,
    snapshots: state.snapshots,
    preferences: normalizePreferences(state.preferences),
  }
}

export function clampDaysAgo(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return 1
  }
  return Math.min(89, Math.max(1, Math.round(number)))
}

export function clampTrendDays(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return 30
  }
  return Math.min(90, Math.max(1, Math.round(number)))
}

export function loadTrackerStorage(storage = globalThis.localStorage) {
  if (!storage) {
    return createDefaultStorageState()
  }

  try {
    const rawValue = storage.getItem(STORAGE_KEY)
    if (!rawValue) {
      return createDefaultStorageState()
    }

    return normalizeTrackerState(JSON.parse(rawValue))
  } catch {
    return createDefaultStorageState()
  }
}

export function saveTrackerStorage(storage = globalThis.localStorage, state) {
  storage.setItem(STORAGE_KEY, JSON.stringify(state))
}

export function loadUserSettings(storage = globalThis.localStorage) {
  if (!storage) {
    return createDefaultUserSettings()
  }

  try {
    const rawValue = storage.getItem(USER_SETTINGS_KEY)
    if (!rawValue) {
      return createDefaultUserSettings()
    }

    return normalizeUserSettings(JSON.parse(rawValue))
  } catch {
    return createDefaultUserSettings()
  }
}

export function hasUserSettings(storage = globalThis.localStorage) {
  if (!storage) {
    return false
  }

  return storage.getItem(USER_SETTINGS_KEY) !== null
}

export function saveUserSettings(storage = globalThis.localStorage, settings) {
  if (!storage) {
    return
  }

  storage.setItem(USER_SETTINGS_KEY, JSON.stringify(normalizeUserSettings(settings)))
}

export function upsertSnapshot(state, snapshot) {
  const nextSnapshots = state.snapshots
    .filter((item) => item.date !== snapshot.date)
    .concat(snapshot)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-90)

  return {
    ...state,
    snapshots: nextSnapshots,
  }
}

export function updatePreferences(state, preferences) {
  return {
    ...state,
    preferences: normalizePreferences({
      ...state.preferences,
      ...preferences,
    }),
  }
}

export function getAllStocks(snapshots) {
  const stocks = new Map()

  snapshots.forEach((snapshot) => {
    snapshot.holdings.forEach((holding) => {
      stocks.set(holding.code, {
        code: holding.code,
        name: holding.name,
        latestWeight: holding.weight,
      })
    })
  })

  return Array.from(stocks.values()).sort((a, b) => a.code.localeCompare(b.code))
}
